import { useState, useMemo } from 'react';
import { type UseFormSetError } from 'react-hook-form';
import {
  useRosterProviders,
  useCreateProvider,
  useUpdateProvider,
  useDeleteProvider,
  useTestConnection,
  useTriggerSync,
  useSyncStatus,
  useUploadCsv,
  useRosterProviderById,
} from '../../../hooks/queries/useRoster';
import type {
  RosterProvider,
  RosterProviderWrite,
  ProviderType,
  SyncMode,
  ConnectionTestResult,
} from '../../../types/RosterTypes';
import { PROVIDER_TYPE_LABELS } from '../../../types/RosterTypes';
import { toaster } from '../../ui/toaster';
import { ApiValidationError } from '../../../lib/http-client';
import type { Column, StatusType } from '@themes/aegix';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

export interface ProviderRow {
  id: string;
  display_name: string;
  provider_type: string;
  status: string;
  last_full_sync: string;
  last_delta_sync: string;
}

export function useRosterCrud() {
  const { t } = useTranslation();

  const STATUS_MAP: Record<string, { label: string; status: StatusType }> = useMemo(() => ({
    active: { label: t('common.active', 'Active'), status: 'active' as StatusType },
    disabled: { label: t('common.disabled', 'Disabled'), status: 'inactive' as StatusType },
    error: { label: t('common.error', 'Error'), status: 'error' as StatusType },
    pending_setup: { label: t('roster.pendingSetup', 'Pending Setup'), status: 'warning' as StatusType },
  }), [t]);

  const columns: Column<ProviderRow>[] = useMemo(() => [
    { key: 'display_name', label: t('common.name', 'Name'), sortable: true, width: '200px' },
    { key: 'provider_type', label: t('common.type', 'Type'), sortable: true, width: '160px' },
    {
      key: 'status',
      label: t('common.status', 'Status'),
      sortable: true,
      width: '140px',
    },
    {
      key: 'last_full_sync',
      label: t('roster.lastFullSync', 'Last Full Sync'),
      sortable: true,
      width: '180px',
    },
    {
      key: 'last_delta_sync',
      label: t('roster.lastDeltaSync', 'Last Delta Sync'),
      sortable: true,
      width: '180px',
    },
  ], [t]);
  const [searchParams] = useSearchParams();
  const urlId = searchParams.get('public_id');
  const urlTab = searchParams.get('subtab');
  const { data: providers, isLoading, error } = useRosterProviders();
  const isRosterTab = urlTab?.toLowerCase().replace(/\s+/g, '-') === 'roster';
  const { data: providerById, error: providerByIdError } =
    useRosterProviderById(urlId as string, isRosterTab);
  const isSingleProviderView = Boolean(
    urlId && isRosterTab && providerById && !providerByIdError,
  );
  const providerDetails =
    isSingleProviderView && providerById ? [providerById] : (providers ?? []);
  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editItem, setEditItem] = useState<RosterProvider | null>(null);

  // Test connection state
  const [testResult, setTestResult] = useState<ConnectionTestResult | null>(
    null,
  );
  const [testingId, setTestingId] = useState<number | null>(null);

  // Sync state
  const [syncDrawerOpen, setSyncDrawerOpen] = useState(false);
  const [syncProvider, setSyncProvider] = useState<RosterProvider | null>(null);

  // Sync history state
  const [historyProvider, setHistoryProvider] = useState<RosterProvider | null>(
    null,
  );

  // CSV upload state
  const [csvDrawerOpen, setCsvDrawerOpen] = useState(false);
  const [csvProvider, setCsvProvider] = useState<RosterProvider | null>(null);

  // Mutations
  const createMutation = useCreateProvider();
  const updateMutation = useUpdateProvider();
  const deleteMutation = useDeleteProvider();
  const testMutation = useTestConnection();
  const syncMutation = useTriggerSync();
  const uploadMutation = useUploadCsv();

  // Sync status query
  const { data: syncStatus } = useSyncStatus(historyProvider?.id ?? null);

  // =========================================================================
  // Handlers
  // =========================================================================

  const handleOpenCreate = () => {
    setEditItem(null);
    setTestResult(null);
    setDrawerOpen(true);
  };

  const handleOpenEdit = (provider: RosterProvider) => {
    setEditItem(provider);
    setTestResult(null);
    setDrawerOpen(true);
  };

  const handleClose = () => {
    setDrawerOpen(false);
    setEditItem(null);
    setTestResult(null);
  };

  const handleSubmit = (
    values: RosterProviderWrite,
    setError: UseFormSetError<RosterProviderWrite>,
  ) => {
    // Strip empty auth_config values
    const cleanAuth: Record<string, string> = {};
    if (values.auth_config) {
      for (const [k, v] of Object.entries(values.auth_config)) {
        if (v) cleanAuth[k] = v;
      }
    }
    const payload = { ...values, auth_config: cleanAuth };

    const onError = (err: Error) => {
      if (err instanceof ApiValidationError) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          setError(field as keyof RosterProviderWrite, {
            message: messages.join(', '),
          });
        }
      }
      toaster.create({
        title: 'Save failed',
        description: err.message,
        type: 'error',
      });
    };

    if (editItem) {
      updateMutation.mutate(
        { configId: editItem.id, data: payload },
        {
          onSuccess: () => {
            toaster.create({ title: 'Provider updated', type: 'success' });
            handleClose();
          },
          onError,
        },
      );
    } else {
      createMutation.mutate(payload, {
        onSuccess: () => {
          toaster.create({ title: 'Provider created', type: 'success' });
          handleClose();
        },
        onError,
      });
    }
  };

  const handleDelete = () => {
    if (!editItem) return;
    deleteMutation.mutate(editItem.id, {
      onSuccess: () => {
        toaster.create({ title: 'Provider deleted', type: 'success' });
        handleClose();
      },
      onError: (err) => {
        toaster.create({
          title: 'Delete failed',
          description: err.message,
          type: 'error',
        });
      },
    });
  };

  const handleTestConnection = (provider: RosterProvider) => {
    setTestingId(provider.id);
    setTestResult(null);
    testMutation.mutate(provider.id, {
      onSuccess: (result) => {
        setTestResult(result);
        setTestingId(null);
        toaster.create({
          title: result.success ? 'Connection successful' : 'Connection failed',
          description: result.message,
          type: result.success ? 'success' : 'error',
        });
      },
      onError: (err) => {
        setTestingId(null);
        toaster.create({
          title: 'Test failed',
          description: err.message,
          type: 'error',
        });
      },
    });
  };

  const handleSync = (mode: SyncMode) => {
    if (!syncProvider) return;
    syncMutation.mutate(
      { configId: syncProvider.id, data: { mode } },
      {
        onSuccess: (result) => {
          toaster.create({
            title: `${mode} sync dispatched`,
            description: result.message,
            type: 'success',
          });
          setSyncDrawerOpen(false);
          setSyncProvider(null);
        },
        onError: (err) => {
          toaster.create({
            title: 'Sync failed',
            description: err.message,
            type: 'error',
          });
        },
      },
    );
  };

  const handleOpenSync = (provider: RosterProvider) => {
    setSyncProvider(provider);
    setSyncDrawerOpen(true);
  };

  const handleCloseSync = () => {
    setSyncDrawerOpen(false);
    setSyncProvider(null);
  };

  const handleOpenHistory = (provider: RosterProvider) => {
    setHistoryProvider(provider);
  };

  const handleCloseHistory = () => {
    setHistoryProvider(null);
  };

  const handleOpenCsv = (provider: RosterProvider) => {
    setCsvProvider(provider);
    setCsvDrawerOpen(true);
  };

  const handleCloseCsv = () => {
    setCsvDrawerOpen(false);
    setCsvProvider(null);
  };

  // =========================================================================
  // DataTable configuration
  // =========================================================================
  const rows: ProviderRow[] = providerDetails
    .filter((p): p is RosterProvider => !!p)
    .map((p) => ({
      id: String(p.id),
      display_name: p.display_name,
      provider_type: PROVIDER_TYPE_LABELS[p.provider_type] ?? p.provider_type,
      status: p.status,
      last_full_sync: p.last_full_sync
        ? new Date(p.last_full_sync).toLocaleString()
        : '\u2014',
      last_delta_sync: p.last_delta_sync
        ? new Date(p.last_delta_sync).toLocaleString()
        : '\u2014',
    }));

  // =========================================================================
  // Initial form values
  // =========================================================================

  const initialValues: RosterProviderWrite = useMemo(
    () =>
      editItem
        ? {
            provider_type: editItem.provider_type,
            display_name: editItem.display_name,
            district: editItem.district,
            base_url: editItem.base_url,
            sync_config: editItem.sync_config ?? {},
            status: editItem.status,
            auth_config: {},
          }
        : {
            provider_type: 'oneroster_1.2' as ProviderType,
            display_name: '',
            district: null,
            base_url: '',
            sync_config: { delta_sync_minutes: 30, full_sync_hours: 24 },
            status: 'pending_setup',
            auth_config: {},
          },
    [editItem],
  );

  return {
    // Data
    providers,
    isLoading,
    error,
    rows,
    columns,
    // Main drawer
    drawerOpen,
    editItem,
    testResult,
    testingId,
    handleOpenCreate,
    handleOpenEdit,
    handleClose,
    handleSubmit,
    handleDelete,
    handleTestConnection,
    initialValues,
    // Sync drawer
    syncDrawerOpen,
    syncProvider,
    syncMutation,
    handleSync,
    handleOpenSync,
    handleCloseSync,
    // History drawer
    historyProvider,
    syncStatus,
    handleOpenHistory,
    handleCloseHistory,
    // CSV drawer
    csvDrawerOpen,
    csvProvider,
    uploadMutation,
    handleOpenCsv,
    handleCloseCsv,
    // Status map (for rendering)
    STATUS_MAP,
  };
}
